// FP3-061 — Provider Workboard constants shared between the page,
// the row chips, and the edit modal. Single source of truth for
// labels + ordering. Adding a new status/source/priority means
// editing exactly this file.

export const SOURCE_OPTIONS = [
  { value: "teacher_referral", label: "Teacher referral" },
  { value: "evaluation_in_process", label: "Evaluation in process" },
  { value: "screening_followup", label: "Screening follow-up" },
  { value: "parent_concern", label: "Parent concern" },
  { value: "provider_note", label: "Provider note" },
  { value: "other", label: "Other" },
];

export const STATUS_OPTIONS = [
  { value: "new", label: "New", tone: "neutral" },
  { value: "waiting_teacher", label: "Waiting on teacher input", tone: "waiting" },
  { value: "waiting_parent", label: "Waiting on parent input", tone: "waiting" },
  { value: "screening_scheduled", label: "Screening scheduled", tone: "active" },
  { value: "evaluation_in_process", label: "Evaluation in process", tone: "active" },
  { value: "review_needed", label: "Review needed", tone: "attention" },
  { value: "complete", label: "Complete", tone: "complete" },
  { value: "not_moving_forward", label: "Not moving forward", tone: "muted" },
];

export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", tone: "muted" },
  { value: "medium", label: "Medium", tone: "neutral" },
  { value: "high", label: "High", tone: "high" },
];

export const SOURCE_LABEL = Object.fromEntries(
  SOURCE_OPTIONS.map((o) => [o.value, o.label])
);
export const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label])
);
export const PRIORITY_LABEL = Object.fromEntries(
  PRIORITY_OPTIONS.map((o) => [o.value, o.label])
);

export const STATUS_TONE_CLASS = {
  neutral: "bg-[#E8E6DE] text-[#3F4A4A] border-[#C9CFC5]",
  waiting: "bg-[#FDF3D8] text-[#7A5C18] border-[#E5C779]",
  active: "bg-[#E1ECDD] text-[#1E3F33] border-[#9FB7A4]",
  attention: "bg-[#FCE8E2] text-[#8A2E15] border-[#E5BBAA]",
  complete: "bg-[#D9E9DC] text-[#1B4A2B] border-[#9FBFA6]",
  muted: "bg-[#F0EEE8] text-[#6E6A60] border-[#D8D4C9]",
};

export const PRIORITY_TONE_CLASS = {
  high: "bg-[#FBE2DA] text-[#8E2415] border-[#E1A290]",
  neutral: "bg-[#E8E6DE] text-[#3F4A4A] border-[#C9CFC5]",
  muted: "bg-[#F0EEE8] text-[#6E6A60] border-[#D8D4C9]",
};

// Helper — return tone class for an option list.
export function toneClass(value, options, table) {
  const found = options.find((o) => o.value === value);
  return table[found?.tone || "neutral"] || table.neutral;
}

// Returns true when the item is "waiting on input" (used for the
// summary card count). Two of the canonical statuses qualify.
export function isWaitingStatus(status) {
  return status === "waiting_teacher" || status === "waiting_parent";
}

// Active = not complete / not_moving_forward / blank.
export function isActiveItem(item) {
  const s = item?.status || "";
  return s !== "complete" && s !== "not_moving_forward";
}

// Due-soon: a follow_up_date OR due_date within the next 7 days
// (inclusive), excluding past dates.
export function isDueSoon(item) {
  const target = item?.follow_up_date || item?.due_date || "";
  if (!target) return false;
  const t = Date.parse(target);
  if (isNaN(t)) return false;
  const now = Date.now();
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  // Same-day items count as due soon.
  return t >= now - 24 * 60 * 60 * 1000 && t <= now + sevenDays;
}
